/*
 OK FBI — OBS Broadcast Overlay
 Transparent, lightweight, host-controlled overlay for the OBS Browser Source.
 Reads /api/overlay/state every 2 seconds. NEVER exposes:
   - culprit / solution
   - unrevealed evidence or suspects (backend strips these)
   - admin controls
 Host toggles visibility of each section via the Overlay Controls panel.
*/
import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { assetUrl } from "@/contexts/SessionContext";
import { sfx } from "@/lib/sounds";
import { useOverlaySfxListener } from "@/components/HostBoards";
import { Trophy, Camera, User, ShieldAlert, Timer, CheckCircle2, XCircle, Vote } from "lucide-react";
import { IntermissionOverlay } from "@/components/Intermission";
import { useSession } from "@/contexts/SessionContext";

const secondsToClock = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}` : `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
};

/**
 * Force full transparency on html / body / #root / .App while the overlay is mounted.
 * Runs synchronously before paint via useLayoutEffect so OBS never sees a white flash.
 * Restored on unmount so navigating back to the host UI still shows the normal background.
 */
function useForceOverlayTransparency() {
  useLayoutEffect(() => {
    const targets = [document.documentElement, document.body, document.getElementById("root")].filter(Boolean);
    const prev = targets.map(el => ({
      el,
      background: el.style.background,
      backgroundColor: el.style.backgroundColor,
      backgroundImage: el.style.backgroundImage,
    }));
    targets.forEach(el => {
      el.style.background = "transparent";
      el.style.backgroundColor = "transparent";
      el.style.backgroundImage = "none";
    });
    document.documentElement.classList.add("okfbi-overlay-mode");
    return () => {
      prev.forEach(({ el, background, backgroundColor, backgroundImage }) => {
        el.style.background = background;
        el.style.backgroundColor = backgroundColor;
        el.style.backgroundImage = backgroundImage;
      });
      document.documentElement.classList.remove("okfbi-overlay-mode");
    };
  }, []);
}

export default function OverlayPage() {
  useForceOverlayTransparency();
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const sessionCtx = useSession();
  const session = sessionCtx?.session || null;
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const ownerId = params.get("owner_user_id") || params.get("streamer") || params.get("user_id") || "";
        const url = ownerId ? `/overlay/state?owner_user_id=${encodeURIComponent(ownerId)}` : "/overlay/state";
        const { data } = await api.get(url);
        if (!stop) setState(data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    // Slightly faster poll (1.5s) — OBS Browser Source is usually foreground; visibility check
    // pauses polling when tab is hidden to save cycles and keep 60fps for the visible browser.
    let t = null, clockT = null;
    const start = () => {
      if (t) return;
      t = setInterval(load, 1500);
      clockT = setInterval(() => setNow(Date.now()), 1000);
    };
    const pause = () => { if (t) { clearInterval(t); t = null; } if (clockT) { clearInterval(clockT); clockT = null; } };
    start();
    const onVis = () => { if (document.hidden) pause(); else { load(); start(); } };
    document.addEventListener("visibilitychange", onVis);
    return () => { stop = true; pause(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Elapsed with local ticking (server-driven with client-side smoothing)
  const [tickBase, setTickBase] = useState({ elapsed: 0, started: 0, running: false });
  useEffect(() => {
    if (!state?.timer) return;
    setTickBase({ elapsed: state.timer.elapsed, started: Date.now(), running: state.timer.running });
  }, [state?.timer?.elapsed, state?.timer?.running]);
  const elapsed = tickBase.running ? tickBase.elapsed + Math.floor((now - tickBase.started) / 1000) : tickBase.elapsed;

  // ============ Live Vote Sound Effect ============
  // Watches ticker[] for new items and plays a subtle chime with rate-limiting (coalesces bursts).
  const seenVoteRef = useRef({ at: null, initialized: false });
  const chimeQueueRef = useRef({ lastAt: 0, pending: 0, timer: null });
  useEffect(() => {
    const cfg = state?.config || {};
    if (!cfg.vote_sound) return;
    const ticker = state?.ticker || [];
    if (!ticker.length) return;
    const newestAt = ticker[0].at;
    // On first mount, remember but do not play
    if (!seenVoteRef.current.initialized) {
      seenVoteRef.current = { at: newestAt, initialized: true };
      return;
    }
    if (seenVoteRef.current.at === newestAt) return;
    // Count how many new votes arrived (they are ordered newest-first)
    let newCount = 0;
    for (const v of ticker) {
      if (v.at === seenVoteRef.current.at) break;
      newCount++;
    }
    seenVoteRef.current.at = newestAt;
    if (newCount <= 0) return;
    // Coalesce: at most 1 chime per 900ms; if more arrive, wait then play once.
    const now = Date.now();
    const vol = Math.max(0, Math.min(1, cfg.vote_sound_volume ?? 0.10));
    const gap = 900;
    if (now - chimeQueueRef.current.lastAt >= gap) {
      sfx.voteChime(vol);
      chimeQueueRef.current.lastAt = now;
    } else if (!chimeQueueRef.current.timer) {
      const remain = gap - (now - chimeQueueRef.current.lastAt);
      chimeQueueRef.current.timer = setTimeout(() => {
        chimeQueueRef.current.timer = null;
        sfx.voteChime(vol);
        chimeQueueRef.current.lastAt = Date.now();
      }, remain);
    }
  }, [state?.ticker, state?.config?.vote_sound, state?.config?.vote_sound_volume]);
  useEffect(() => () => { if (chimeQueueRef.current.timer) clearTimeout(chimeQueueRef.current.timer); }, []);

  // Investigator sound pack — fires on overlay when host triggers a stinger
  useOverlaySfxListener(state);

  if (!state) return <div className="overlay-root" dir="rtl" data-testid="overlay-loading" />;
  const cfg = state.config || {};
  const pos = cfg.position || "bottom-left";
  const size = cfg.size || "md";

  // ============ INTERMISSION MODE ============
  // Backend strips all case data during intermission; we take over the full viewport.
  if (state.intermission?.active) {
    return <IntermissionOverlay intermission={state.intermission} session={session} />;
  }

  const posClass = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "full": "inset-4",
  }[pos];

  const sizeClass = { sm: "w-72 text-[11px]", md: "w-96 text-xs", lg: "w-[440px] text-sm" }[size];

  return (
    <div className="overlay-root fixed inset-0 pointer-events-none" dir="rtl" data-testid="broadcast-overlay">
      <style>{`
        html, body, #root { background: transparent !important; }
        .overlay-panel { background: linear-gradient(180deg, rgba(10,10,12,0.86), rgba(6,6,8,0.9)); backdrop-filter: blur(10px); border: 1px solid rgba(197,160,89,0.35); box-shadow: 0 4px 30px rgba(0,0,0,0.6); }
        .overlay-gold { color: #C5A059; }
        .overlay-metal { border-color: rgba(197,160,89,0.35); }
      `}</style>
      <div className={`absolute ${posClass} ${sizeClass} font-cairo`} data-testid="overlay-container">
        {/* CASE HEADER */}
        {cfg.show_case !== false && state.case && (
          <div className="overlay-panel px-4 py-3 mb-2 flex items-center gap-3" data-testid="overlay-case">
            {state.case.cover && <img src={assetUrl(state.case.cover)} className="w-12 h-12 object-cover border border-gold/40" alt="" />}
            <div className="flex-1 min-w-0">
              <div className="text-[9px] tracking-[0.35em] overlay-gold font-mono-ar">CASE {state.case.code}</div>
              <div className="text-white font-cairo truncate text-lg leading-tight">{state.case.title}</div>
              <div className="text-[10px] text-zinc-400 truncate">{state.case.location}</div>
            </div>
            {cfg.show_timer !== false && state.timer && (
              <div className="text-left border-l border-metal/50 pl-3">
                <div className="text-[9px] font-mono-ar text-zinc-400">TIMER</div>
                <div className="overlay-gold font-mono-ar tabular-nums text-xl">{secondsToClock(elapsed)}</div>
              </div>
            )}
          </div>
        )}

        {/* PHASE */}
        {cfg.show_phase !== false && (
          <div className="overlay-panel px-3 py-1.5 mb-2 flex items-center justify-between" data-testid="overlay-phase">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-mono-ar text-zinc-300">{phaseLabel(state.phase)}</span>
            </div>
            <span className="text-[9px] font-mono-ar overlay-gold">OK FBI · LIVE</span>
          </div>
        )}

        {/* CUE CARD BROADCAST (host-pushed, spoiler-safe: host-authored text only) */}
        {state.cue_card && state.cue_card.text && (
          <div className="overlay-panel px-3 py-2 mb-2" data-testid="overlay-cue-card">
            <div className="text-[9px] tracking-[0.3em] font-mono-ar overlay-gold mb-0.5">TRANSMISSION</div>
            <div className="text-white text-[13px] leading-relaxed font-cairo">{state.cue_card.text}</div>
          </div>
        )}

        {/* INTERROGATION TIMER (spoiler-safe: suspect name + countdown only) */}
        {state.interrogation_timer && (
          <InterrogationTimerOverlay t={state.interrogation_timer} />
        )}

        {/* FEATURED SUSPECT */}
        {cfg.show_suspect !== false && state.featured_suspect && (
          <div className="overlay-panel p-3 mb-2 flex items-center gap-3" data-testid="overlay-suspect">
            <img src={assetUrl(state.featured_suspect.portrait)} alt="" className="w-16 h-16 object-cover border border-gold/50" />
            <div>
              <div className="text-[9px] tracking-widest overlay-gold font-mono-ar">SUSPECT · {state.featured_suspect.id}</div>
              <div className="text-white text-lg font-cairo">{state.featured_suspect.name}</div>
              <div className="text-[10px] text-zinc-400">{state.featured_suspect.job}</div>
            </div>
          </div>
        )}

        {/* FEATURED EVIDENCE */}
        {cfg.show_evidence !== false && state.featured_evidence && (
          <div className="overlay-panel p-3 mb-2" data-testid="overlay-evidence">
            <div className="flex justify-between items-start mb-1">
              <div className="text-[9px] tracking-widest overlay-gold font-mono-ar">EVIDENCE · {state.featured_evidence.id}</div>
            </div>
            <div className="text-white font-cairo">{state.featured_evidence.name}</div>
            <div className="text-[10px] text-zinc-400 line-clamp-2 mt-1">{state.featured_evidence.description}</div>
          </div>
        )}

        {/* REVEALED EVIDENCE STRIP */}
        {cfg.show_evidence !== false && !state.featured_evidence && state.revealed_evidence.length > 0 && (
          <div className="overlay-panel p-3 mb-2" data-testid="overlay-evidence-list">
            <div className="text-[9px] tracking-widest overlay-gold font-mono-ar mb-1">أدلة مكشوفة · {state.revealed_evidence.length}</div>
            <div className="flex flex-wrap gap-1">
              {state.revealed_evidence.slice(-8).map(e => (
                <span key={e.id} className="text-[10px] px-1.5 py-0.5 border border-gold/40 text-gold font-mono-ar">{e.id}</span>
              ))}
            </div>
          </div>
        )}

        {/* VOTING */}
        {cfg.show_voting !== false && state.voting && state.voting.rows.length > 0 && (
          <div className="overlay-panel p-3 mb-2" data-testid="overlay-voting">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] tracking-widest overlay-gold font-mono-ar flex items-center gap-1"><Vote size={10} /> تصويت المشاهدين</div>
              <div className={`text-[9px] font-mono-ar ${state.voting.open ? "text-emerald-400" : "text-zinc-500"}`}>{state.voting.open ? "OPEN" : "CLOSED"} · {state.voting.total} أصوات</div>
            </div>
            <div className="space-y-1">
              {state.voting.rows.slice(0, 4).map(r => (
                <div key={r.id} className="text-[11px]">
                  <div className="flex justify-between mb-0.5">
                    <span className="text-white truncate flex-1">{r.name}</span>
                    <span className="overlay-gold font-mono-ar">{cfg.show_percent !== false ? `${r.percent}%` : `${r.count}`}</span>
                  </div>
                  <div className="h-1 bg-black/40 border border-metal/40 relative overflow-hidden">
                    <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-yellow-700 to-yellow-500" style={{ width: `${Math.min(100, r.percent)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RESULT */}
        {cfg.show_result !== false && state.result && (
          <div className={`overlay-panel p-3 mb-2 border-2 ${state.result.status === "solved" ? "border-emerald-500" : "border-red-500"}`} data-testid="overlay-result">
            <div className="flex items-center gap-2">
              {state.result.status === "solved" ? <CheckCircle2 className="text-emerald-400" size={20} /> : <XCircle className="text-red-400" size={20} />}
              <div className="flex-1">
                <div className="text-[9px] tracking-widest font-mono-ar text-zinc-400">{state.result.status === "solved" ? "CASE SOLVED" : "CASE UNSOLVED"}</div>
                <div className={`font-cairo text-xl ${state.result.status === "solved" ? "text-emerald-300" : "text-red-300"}`}>
                  {state.result.status === "solved" ? "تم حل القضية" : "لم يتم حل القضية"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TOP INVESTIGATORS */}
        {cfg.show_leaderboard !== false && state.top && state.top.length > 0 && (
          <div className="overlay-panel p-3" data-testid="overlay-leaderboard">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={11} className="overlay-gold" />
              <div className="text-[9px] tracking-widest overlay-gold font-mono-ar">TOP INVESTIGATORS</div>
            </div>
            <div className="space-y-1">
              {state.top.slice(0, 5).map(r => (
                <div key={r.name} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`font-mono-ar text-xs w-5 text-center ${r.rank === 1 ? "text-gold" : "text-zinc-400"}`}>#{r.rank}</span>
                    <span className="text-white truncate">{r.name}</span>
                  </div>
                  <span className="overlay-gold font-mono-ar tabular-nums">{r.total_score}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* LIVE VOTE TICKER — scrolling at the bottom */}
      {cfg.show_ticker !== false && state.ticker && state.ticker.length > 0 && (
        <VoteTicker items={state.ticker} hideNames={!!cfg.hide_voter_names} />
      )}

      {/* MEETING ROOM MIRROR (opt-in via host toggle) */}
      {cfg.show_meeting_room && <OverlayMeetingRoom pos={pos} />}

      {/* Watermark bottom-right */}
      <div className="absolute bottom-1 right-2 text-[9px] tracking-[0.3em] text-zinc-500/70 font-mono-ar">OK FBI · LIVE</div>
    </div>
  );
}

// ============ OVERLAY MEETING ROOM (broadcast-safe mirror) ============
// Reads /api/tiktok/meeting-room every ~2s. Never displays webhook_secret,
// provider, error strings, or any host-only diagnostic.
function OverlayMeetingRoom({ pos }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = () => api.get("/tiktok/meeting-room?limit=10").then(({ data }) => { if (!stop) setData(data); }).catch(() => {});
    load(); const iv = setInterval(load, 2000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  if (!data || !data.overlay_show) return null;
  const comments = (data.comments || []).slice().reverse();
  // Place opposite of the main panel so it doesn't overlap
  const posClass = pos && pos.includes("right") ? "left-4 bottom-4" : "right-4 bottom-4";
  return (
    <div className={`fixed ${posClass} w-80 pointer-events-none`} dir="rtl" data-testid="overlay-meeting-room">
      <div className="overlay-panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.25em] overlay-gold font-cairo">غرفة العمليات</div>
          <div className={`text-[9px] font-mono-ar ${data.real_live ? "text-emerald-400" : "text-zinc-500"}`}>
            {data.real_live ? "● LIVE" : "● OFFLINE"} · @{data.username || "—"}
          </div>
        </div>
        <div className="space-y-1 max-h-[220px] overflow-hidden">
          {comments.length === 0 && <div className="text-[11px] text-zinc-500 text-center py-4">لا توجد تعليقات بعد</div>}
          {comments.slice(-6).map(c => (
            <div key={c.id} className={`text-[11px] leading-tight ${c.matched ? "border-r border-r-gold/50 pr-2" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="overlay-gold truncate max-w-[130px]">{c.display_name || c.username}</span>
                <span className="text-zinc-600 text-[9px] font-mono-ar">{new Date(c.at).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <div className="text-white font-cairo truncate">{c.text}</div>
              {c.matched && (
                <div className="text-[9px] text-emerald-300 font-cairo">
                  {c.prev_matched && c.prev_matched !== c.matched ? "غيّر تصويته" : c.duplicate_vote ? "مسجل مسبقاً" : "✓ تم تسجيل التصويت"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VoteTicker({ items, hideNames }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 pointer-events-none" data-testid="overlay-ticker" dir="rtl">
      <div className="mx-auto max-w-[1400px] px-2 py-1.5" style={{ background: "linear-gradient(90deg, rgba(6,6,8,0.95) 0%, rgba(10,10,12,0.85) 50%, rgba(6,6,8,0.95) 100%)", borderTop: "1px solid rgba(197,160,89,0.4)" }}>
        <div className="flex items-center gap-3 overflow-hidden">
          <span className="text-[9px] tracking-[0.35em] overlay-gold font-mono-ar shrink-0 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE VOTES
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="flex gap-6 whitespace-nowrap animate-marquee">
              {[...items, ...items].map((v, i) => (
                <span key={i} className="text-[11px] font-cairo text-zinc-200 inline-flex items-center gap-1.5">
                  {!hideNames && <span className="text-white">{v.participant || "مشاهد"}</span>}
                  <span className="text-zinc-500">←</span>
                  <span className="overlay-gold">{v.suspect_name}</span>
                  {v.changed && <span className="text-amber-400 text-[9px] font-mono-ar">(تحويل)</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 40s linear infinite; }
      `}</style>
    </div>
  );
}

function phaseLabel(p) {
  return ({
    briefing: "الإعداد",
    investigating: "جاري التحقيق",
    voting: "التصويت مفتوح",
    accused: "تم توجيه الاتهام",
    solved: "تم حل القضية",
    failed: "لم يتم حل القضية",
    idle: "في الانتظار",
  })[p] || "جاري التحقيق";
}

// Cinematic interrogation countdown overlay — synced via /overlay/state (server authoritative)
// Locally smoothed at 4Hz. Progress bar fills right→left (RTL).
function InterrogationTimerOverlay({ t }) {
  const [now, setNow] = useState(Date.now());
  const [base, setBase] = useState({ started: Date.now(), secondsLeft: t.seconds_left || 0, running: !!t.running });
  useEffect(() => {
    setBase({ started: Date.now(), secondsLeft: t.seconds_left || 0, running: !!t.running });
  }, [t.seconds_left, t.running, t.q_index, t.suspect_id]);
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);
  const remaining = base.running
    ? Math.max(0, Math.round(base.secondsLeft - (now - base.started) / 1000))
    : base.secondsLeft;
  const dur = t.duration || 30;
  const pct = Math.max(0, Math.min(100, (remaining / dur) * 100));
  const critical = base.running && remaining <= 5;
  const expired = !base.running && remaining === 0 && !!t.duration;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return (
    <div className={`overlay-panel px-3 py-2 mb-2 ${critical ? "border-red-500 animate-pulse" : expired ? "border-red-800" : ""}`} data-testid="overlay-interrogation-timer">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Timer size={12} className={critical ? "text-red-400" : "overlay-gold"} />
          <div className="text-[9px] tracking-[0.3em] font-mono-ar text-zinc-300">استجواب {t.suspect_name || ""}</div>
        </div>
        <div className={`font-mono-ar tabular-nums text-xl ${critical ? "text-red-400" : "overlay-gold"}`}>{mm}:{ss}</div>
      </div>
      <div className="mt-1 h-1.5 bg-black/50 border border-metal/40 relative overflow-hidden">
        <div className={`absolute inset-y-0 right-0 ${critical ? "bg-gradient-to-l from-red-600 to-red-400" : "bg-gradient-to-l from-yellow-700 to-yellow-500"}`}
             style={{ width: `${pct}%`, transition: "width 250ms linear" }} />
      </div>
      {expired && <div className="text-[9px] font-mono-ar text-red-400 mt-1 text-center">⚠ انتهى الوقت — TIME UP</div>}
    </div>
  );
}
